import { CustomFieldEntityType, CustomField, CustomFieldValue, CustomFieldUtil } from '../../../../types/custom_field';
import { toMap } from '../../../../util/map.util';
import { PdfDataKey, PdfFieldEntity, PdfSubkey } from './PdfDataKey';

function pdfFieldEntityTypeToCustomFieldEntityType(entityType: PdfFieldEntity): CustomFieldEntityType | null {
  switch (entityType) {
    case PdfFieldEntity.bin:
      return CustomFieldEntityType.bin;
    case PdfFieldEntity.container:
      return CustomFieldEntityType.container;
    case PdfFieldEntity.productMasterData:
      return CustomFieldEntityType.productMasterData;
    case PdfFieldEntity.lot:
      return CustomFieldEntityType.lot;
    case PdfFieldEntity.order:
      return CustomFieldEntityType.order;
    case PdfFieldEntity.productTransaction:
      return CustomFieldEntityType.productTransaction;
    case PdfFieldEntity.stockLocation:
    case PdfFieldEntity.pdf:
      return null;
  }
}

export abstract class PdfEntityDataResolver<T, P> {
  abstract properties: P[];
  abstract entityType: PdfFieldEntity;

  constructor() {}

  abstract getCustomFieldByEntity(entity: T, id: string): CustomFieldValue | undefined;

  isDataKeyValidForImage(key: PdfDataKey<P>, customFields: Map<string, CustomField>): boolean {
    return false;
  }

  hasSubkey(property: P, subkey: PdfSubkey): boolean {
    return true;
  }

  getDataKeys(customFields: Map<string, CustomField>, subkey?: PdfSubkey, image: boolean = false): PdfDataKey<P>[] {
    const customFieldEntityType = pdfFieldEntityTypeToCustomFieldEntityType(this.entityType);
    const customFieldDataKeys: PdfDataKey<P>[] = [...customFields.values()]
      .filter(cf => {
        if (cf.entityType !== customFieldEntityType) return false;
        return true;
      })
      .map(oc => ({
        entityType: this.entityType,
        customFieldId: oc.id,
        subkey,
      }));

    const propertyDataKeys = this.properties.map((property: P) => {
      return {
        entityType: this.entityType,
        property,
        subkey: subkey && this.hasSubkey(property, subkey) ? subkey : undefined,
      };
    });

    const datakeys = [...customFieldDataKeys, ...propertyDataKeys];
    if (image) return datakeys.filter(dk => this.isDataKeyValidForImage(dk, customFields));
    return datakeys;
  }

  getDataKeysWithSubkeys(
    customFields: Map<string, CustomField>,
    subkeys: PdfSubkey[],
    image: boolean = false,
  ): PdfDataKey<P>[] {
    const map = toMap(
      subkeys.flatMap(subkey => this.getDataKeys(customFields, subkey, image)),
      dk => JSON.stringify(dk),
    );
    return [...map.values()];
  }

  // getImageDatakeys(customFields: Map<string, CustomField>, subkeys?: PdfSubkey[]): PdfDataKey<P>[] {
  //   const datakeys = subkeys ? this.getDataKeysWithSubkeys(customFields, subkeys) : this.getDataKeys(customFields);
  //   return datakeys.filter(dk => this.isDataKeyValidForImage(dk, customFields));
  // }

  abstract getLabelByProperty(property: P): string;

  getLabelByDataKey(customFields: Map<string, CustomField>, key: PdfDataKey<P>): string {
    let value;
    if ('customFieldId' in key) {
      const customField = customFields.get(key.customFieldId);
      if (!customField) return 'Unknown Custom Field';
      value = customField.name;
    } else {
      value = this.getLabelByProperty(key.property);
    }

    if (!key.subkey) return value;

    return `${value} (${key.subkey})`;
  }

  abstract getValueByProperty(property: P, entity: T): string;

  getValueByDataKey(key: PdfDataKey<P>, entity: T): string {
    if ('customFieldId' in key) {
      const value = this.getCustomFieldByEntity(entity, key.customFieldId);
      if (!value) return 'No value';
      return CustomFieldUtil.getTextForCustomField(value, 'en-GB');
    }

    return this.getValueByProperty(key.property, entity);
  }
}
